import React, { useEffect, useState } from 'react';
import { CompactTable } from '@table-library/react-table-library/compact';
import { useTheme } from '@table-library/react-table-library/theme';
import { getTheme } from '@table-library/react-table-library/baseline';
import { useSort } from '@table-library/react-table-library/sort';
import { usePagination } from '@table-library/react-table-library/pagination';
import { useTranslation } from 'react-i18next';
import { useTheme as useCustomTheme } from '@mui/material';
import { useStream } from '../../../hooks/useStream';
import StateService from '../../../services/stateService/StateService';
import DemoPreview from '../../demo-preview/DemoPreview';
import { useStyles } from './styles';
import CustomSelect from '../../custom-select/CustomSelect';
import EmptyState from '../../emptyState/EmptyState';
import ChartService from '../../../services/utilService/ChartService';
import { WidgetData } from '../../../models/WidgetData';
import { async } from 'rxjs';
import WidgetService from '../../../services/widgetService/WidgetService';

type Props = {
    data: WidgetData;
};

const dummyData = {
    labels: ['0', '1', '2', '3', '4', '5', '6'],
    labelsTypeId: 1,
    datasets: [
        {
            label: 'AIGateway.tokenCountRequest',
            data: [3247902, 2438919, 3535771, 2856189, 3534310, 5696586, 4974283],
        },
        {
            label: 'AIGateway.tokenCountCompletion',
            data: [351873, 265295, 388178, 312798, 394261, 621626, 542688],
        },
        {
            label: 'AIGateway.tokenCountTotal',
            data: [3599775, 2704214, 3923949, 3168987, 3928571, 6318212, 5516971],
        },
    ],
};

const CustomDataTable = ({ data }: Props) => {
    const classes = useStyles();
    const [t] = useTranslation();
    const [selectedWidgetData, setSelectedWidgetData] = useStream(StateService.selectedWidgetData$);
    const demoTitle = 'DEMO_DATA.DEMO_TITLE_LABEL';
    const customTheme = useCustomTheme();
    const evenColor = customTheme.palette.neutral[100];
    const rowsPerPage = [
        { name: '5', value: 5 },
        { name: '10', value: 10 },
        { name: '15', value: 15 },
    ];
    const [selectedRpp, setSelectedRpp] = useState('-1');
    const selectMaxHeight = '500px';
    const [chartData, setChartData] = useState(data?.chartData);
    const usedData = ChartService.prepareChartData(chartData || data?.chartData || dummyData, t) || { labels: [], datasets: [] };
    const [customerUuid, setCustomerUuid] = useStream(StateService.currentCustomerUuid$);
    const [isEditorOpen, setIsEditorOpen] = useStream(StateService.isEditorOpen$);
    const abortController = new AbortController();
    const [updateInterval, _setUpdateInterval] = useState(20000);

    const nodes = usedData?.labels?.map((label, index) => {
        const rowData = usedData?.datasets.reduce((acc, dataset) => {
            acc[dataset.label] = dataset.data[index] || 0; // Default to 0 if data is missing
            return acc;
        }, {});
        return {
            id: label,
            ...rowData,
        };
    });

    useEffect(() => {
        setChartData(data?.chartData);
    }, [data?.chartData]);

    useEffect(() => {
        if (!isEditorOpen) {
            const intervalId = setInterval(() => {
                getResourceValues();
            }, updateInterval);

            return () => {
                clearInterval(intervalId);
            };
        }
        return () => {};
    }, [
        updateInterval,
        customerUuid,
        data?.selectedResourceInput,
        data?.selectedQueryPeriodInput,
        data?.selectedDimensionInput,
        isEditorOpen,
    ]);

    const getResourceValues = async () => {
        if (isEditorOpen) return;

        const res = await WidgetService.getResourceData(
            customerUuid,
            data?.selectedResourceInput,
            data?.selectedQueryPeriodInput,
            data?.selectedDimensionInput,
            { signal: abortController.signal },
        );

        const updatedWidgetData = {
            ...selectedWidgetData,
            barChartResourceId: data?.selectedResourceInput,
            selectedResourceInput: data?.selectedResourceInput,
            selectedQueryPeriodInput: data?.selectedQueryPeriodInput,
            selectedDimensionInput: data?.selectedDimensionInput,
            chartData: res?.data,
        };

        // Update the state with the updated widget data
        setSelectedWidgetData(updatedWidgetData);
    };

    const tableData = { nodes };

    const parseDateToMilliseconds = (dateString) => {
        const [day, month, year] = dateString.split('.');
        return Date.parse(`${year}-${month}-${day}`);
    };

    const COLUMNS = [
        { label: 'ID', sort: { sortKey: 'id' }, renderCell: (item) => item.id, resize: true },
        ...usedData.datasets.map((dataset) => ({
            label: t(dataset.label.toUpperCase()),
            sort: { sortKey: dataset.label },
            renderCell: (item) => item[dataset.label],
            resize: true,
        })),
    ];

    const sortFns: any = usedData?.datasets.reduce(
        (acc, dataset) => {
            acc[dataset.label] = (array) => array.sort((a, b) => a[dataset.label] - b[dataset.label]);
            return acc;
        },
        {
            id: (array) => {
                switch (chartData?.labelsType) {
                    case 'WEEKDAYS':
                        const labelOrder = usedData.labels;
                        return array.sort((a, b) => labelOrder.indexOf(a.id) - labelOrder.indexOf(b.id));
                    case 'DATES':
                        // parse to ms to sort dates
                        return array.sort((a, b) => parseDateToMilliseconds(a.id) - parseDateToMilliseconds(b.id));
                    default:
                        return array.sort((a, b) => a.id.localeCompare(b.id));
                }
            },
        },
    );

    const sort = useSort(
        tableData,
        {
            state: { sortKey: 'id', reverse: false },
        },
        {
            sortFns,
        },
    );

    const pagination = usePagination(tableData, {
        state: {
            page: 0,
            size: selectedRpp !== '-1' ? selectedRpp : 5,
        },
    });

    const startSize = pagination.state.page * pagination.state.size + 1;
    const endSize = Math.min(startSize + pagination.state.size - 1, tableData.nodes.length);
    const total = tableData.nodes.length;

    const theme = useTheme([
        getTheme(),
        {
            Table: `
                height: unset
            `,
            HeaderRow: `
            background-color: ${customTheme.palette.grey[200]};
            color: ${customTheme.palette.text.primary};

            & th {
                padding: ${customTheme.spacing(2)}px;
            }

        `,
            Row: `
            & td {
                max-height: ${customTheme.spacing(3)}px;
                padding: ${customTheme.spacing(3)}px;
            }
            &:nth-of-type(odd) {
                background-color: ${customTheme?.palette.neutral[50]};
            }
            &:nth-of-type(even) {
                background-color: ${evenColor};
            }
            color: ${customTheme.palette.text.secondary};
        `,
        },
    ]);

    const handleRppChange = (event) => {
        const value = event?.target?.value;
        setSelectedRpp(value);
    };

    if (data?.isUsingDefault) {
        return <DemoPreview title={demoTitle} isDataTable={data?.isUsingDefault} />;
    }

    if (!usedData?.labels?.length || !usedData?.datasets?.length) {
        return <EmptyState noData={true} />;
    }

    return (
        <>
            {/* key property forces react to render the table completely new if it changes */}
            {data && (
                <CompactTable
                    key={usedData?.datasets.length}
                    theme={theme}
                    columns={COLUMNS}
                    data={tableData}
                    sort={sort}
                    pagination={pagination}
                    layout={{ fixedHeader: true }}
                />
            )}{' '}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className={classes.rppWrapper}>
                    <label>{t('WIDGET_EDITOR.DATA_TABLE_ROWS_PER_PAGE')}</label>
                    <CustomSelect
                        options={rowsPerPage.map((option, index) => ({
                            value: option?.value,
                            label: option?.name,
                        }))}
                        value={selectedRpp || '-1'}
                        onChange={handleRppChange}
                        onBlur={handleRppChange}
                        id="data-entity"
                        isFilterable={false}
                        dataTestId="data-entity"
                        nAplaceholder={t('WIDGET_EDITOR.NOT_SELECTED_DROPDOWN')}
                        isEmpty={false}
                        errorText={t('WIDGET_EDITOR.DATA_ENTITY_ERROR_LABEL')}
                        menuPaperStyle={{ maxHeight: selectMaxHeight, width: 'fit-content' }}
                        width="fit-content"
                    />
                </div>
                <span>
                    {startSize}
                    {'-'}
                    {endSize} {t('COMMON.TABLE_FOOTER_OF_LABEL')} {total} {' Items'}
                </span>
                <span>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <span>
                            {t('CHART_DATA.TOTAL_PAGES')}: {pagination.state.getTotalPages(tableData.nodes)}
                        </span>
                        <div className={classes.footerButtonWrapper}>
                            <button
                                className={classes.movePageBtn}
                                type="button"
                                disabled={pagination.state.page === 0}
                                onClick={() => pagination.fns.onSetPage(0)}
                            >
                                {'|<'}
                            </button>
                            <button
                                type="button"
                                disabled={pagination.state.page === 0}
                                onClick={() => pagination.fns.onSetPage(pagination.state.page - 1)}
                                className={classes.movePageBtn}
                            >
                                {'<' + t('CHART_DATA.PREVIOUS')}
                            </button>
                            {pagination.state.getPages(tableData.nodes).map((_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={classes.numberPageBtn}
                                    style={{
                                        padding: customTheme.spacing(0.5, 1),
                                        fontWeight: pagination.state.page === index ? 'bold' : 'normal',
                                    }}
                                    onClick={() => pagination.fns.onSetPage(index)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                            <button
                                className={classes.movePageBtn}
                                type="button"
                                disabled={pagination.state.page + 1 === pagination.state.getTotalPages(tableData.nodes)}
                                onClick={() => pagination.fns.onSetPage(pagination.state.page + 1)}
                            >
                                {t('CHART_DATA.NEXT') + '>'}
                            </button>
                            <button
                                className={classes.movePageBtn}
                                type="button"
                                disabled={pagination.state.page + 1 === pagination.state.getTotalPages(tableData.nodes)}
                                onClick={() => pagination.fns.onSetPage(pagination.state.getTotalPages(tableData.nodes) - 1)}
                            >
                                {'>|'}
                            </button>
                        </div>
                    </div>
                </span>
            </div>
        </>
    );
};

export default CustomDataTable;
